import { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiClient } from '../../services/api'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useTranslation } from 'react-i18next'
import { useProfile } from '../../hooks/useProfile'

export function Navbar() {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const { user, clearAuth } = useAuthStore()
  const { profile } = useProfile()
  const navigate = useNavigate()

  async function handleLogout() {
    await apiClient.post('/api/auth/logout')
    clearAuth()
    navigate('/login')
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'text-accent-400 font-semibold'
      : 'text-brand-100 hover:text-white transition-colors'

  return (
    <nav className="bg-brand-700 text-white shadow-md">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center">
        {/* Logo — left */}
        <Link to="/" className="flex items-center gap-2 font-bold text-lg tracking-tight shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
            className="w-6 h-6 text-accent-400">
            <path fillRule="evenodd"
              d="M14.615 1.595a.75.75 0 0 1 .359.852L12.982 9.75h7.268a.75.75 0 0 1 .548 1.262l-10.5 11.25a.75.75 0 0 1-1.272-.71l1.992-7.302H3.75a.75.75 0 0 1-.548-1.262l10.5-11.25a.75.75 0 0 1 .913-.143Z"
              clipRule="evenodd" />
          </svg>
          Kilterinlinna
        </Link>

        {/* Desktop nav — center links */}
        {user && (
          <div className="hidden sm:flex items-center justify-center gap-6 text-sm flex-1">
            {user.is_user && (
              <>
                <NavLink to="/dashboard" className={navLinkClass}>{t('nav.dashboard')}</NavLink>
                <NavLink to="/sessions" className={navLinkClass}>{t('nav.sessions')}</NavLink>
                <NavLink to="/contracts" className={navLinkClass}>{t('nav.contracts')}</NavLink>
                <NavLink to="/subusers" className={navLinkClass}>{t('nav.subUsers')}</NavLink>
              </>
            )}
            {user.is_admin && (
              <NavLink to="/admin" className={navLinkClass}>{t('nav.admin')}</NavLink>
            )}
            <NavLink to="/ohje" className={navLinkClass}>{t('nav.guide')}</NavLink>
          </div>
        )}

        {/* Right side — language + logout */}
        <div className="hidden sm:flex items-center gap-4 text-sm shrink-0 ml-auto">
          <LanguageSwitcher />
          {user && profile && (
            <span className="text-brand-200 text-xs">{profile.displayName}</span>
          )}
          {user && (
            <button onClick={handleLogout}
              className="text-brand-100 hover:text-white transition-colors">
              {t('nav.logout')}
            </button>
          )}
        </div>

        {/* Mobile: language (when not logged in) + hamburger */}
        <div className="sm:hidden flex items-center gap-3 ml-auto">
          {!user && <LanguageSwitcher />}
          {user && (
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="p-1 rounded focus:outline-none focus:ring-2 focus:ring-accent-400"
              aria-label={t('nav.menu')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {user && menuOpen && (
        <div className="sm:hidden bg-brand-700 border-t border-brand-600 px-4 pb-4 flex flex-col gap-3 text-sm">
          {user.is_user && (
            <>
              <NavLink to="/dashboard" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.dashboard')}</NavLink>
              <NavLink to="/sessions" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.sessions')}</NavLink>
              <NavLink to="/contracts" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.contracts')}</NavLink>
              <NavLink to="/subusers" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.subUsers')}</NavLink>
            </>
          )}
          {user.is_admin && (
            <NavLink to="/admin" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.admin')}</NavLink>
          )}
          <NavLink to="/ohje" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.guide')}</NavLink>
          <LanguageSwitcher />
          {profile && <span className="text-brand-200 text-xs">{profile.displayName}</span>}
          <button onClick={handleLogout} className="text-left text-brand-100 hover:text-white transition-colors">
            {t('nav.logout')}
          </button>
        </div>
      )}
    </nav>
  )
}
